import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useCollection } from '@/hooks/useCollection';
import { usePinnedCards, usePinCard, useUnpinCard } from '@/hooks/usePinnedCards';
import { InkDot } from '@/components/InkDot';
import { CardImage } from '@/components/CardImage';
import { MatchHistoryCard } from '@/components/MatchHistoryCard';
import { DeckBreakdownCard } from '@/components/DeckBreakdownCard';
import { AVATAR_OPTIONS, getAvatarById } from '@/lib/avatar-options';
import { LogOut, MapPin, Calendar, Pin, X, Edit2, Check, Trophy, Sparkles, Settings } from 'lucide-react';
import type { InkColor } from '@/lib/ink-utils';
import { toast } from 'sonner';

const ACHIEVEMENTS = [
  { id: 'first-card', label: 'First Card', emoji: '🃏', check: (total: number) => total >= 1 },
  { id: 'collector-10', label: '10 Cards', emoji: '📦', check: (total: number) => total >= 10 },
  { id: 'collector-50', label: '50 Cards', emoji: '🏆', check: (total: number) => total >= 50 },
  { id: 'collector-100', label: 'Centurion', emoji: '💯', check: (total: number) => total >= 100 },
  { id: 'enchanted-1', label: 'Enchanted!', emoji: '✨', check: (_t: number, e: number) => e >= 1 },
  { id: 'enchanted-5', label: 'Sparkle Master', emoji: '🌟', check: (_t: number, e: number) => e >= 5 },
];

export function ProfileTab() {
  const { profile, signOut, updateProfile } = useAuth();
  const { data: collection } = useCollection();
  const { data: pinnedCards } = usePinnedCards();
  const pinCard = usePinCard();
  const unpinCard = useUnpinCard();
  const [showPinPicker, setShowPinPicker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBio, setEditBio] = useState(profile?.bio || '');
  const [editAvatar, setEditAvatar] = useState(profile?.avatar_emoji || 'sorcerer');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const initialInks: InkColor[] = ((profile as any)?.favorite_inks?.length
    ? (profile as any).favorite_inks
    : (profile as any)?.favorite_ink
    ? [(profile as any).favorite_ink]
    : []) as InkColor[];
  const [editInks, setEditInks] = useState<InkColor[]>(initialInks);
  const [savingInks, setSavingInks] = useState(false);

  const INK_OPTIONS: InkColor[] = ['Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'];
  function toggleEditInk(ink: InkColor) {
    setEditInks((curr) => {
      if (curr.includes(ink)) return curr.filter((i) => i !== ink);
      if (curr.length >= 2) return [curr[1], ink];
      return [...curr, ink];
    });
  }
  async function saveInks() {
    if (editInks.length !== 2) {
      toast.error('Pick exactly 2 favorite inks');
      return;
    }
    setSavingInks(true);
    try {
      await updateProfile({
        favorite_inks: editInks,
        favorite_ink: editInks[0],
      } as any);
      toast.success('Favorite inks updated');
      setShowSettings(false);
    } catch (err: any) {
      toast.error(err.message || 'Could not update');
    } finally {
      setSavingInks(false);
    }
  }

  if (!profile) return null;

  const totalCards = (collection || []).reduce((acc, c) => acc + (c.quantity || 1), 0);
  const uniqueCards = (collection || []).length;
  const enchantedCount = (collection || []).filter(c => c.card?.enchanted).length;
  const setsOwned = new Set((collection || []).map(c => c.card?.set_name).filter(Boolean)).size;
  const joinDate = new Date(profile.joined_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  const unlockedAchievements = ACHIEVEMENTS.filter(a => a.check(totalCards, enchantedCount));
  const pinnedCardIds = new Set((pinnedCards || []).map(p => p.card_id));

  const currentAvatar = getAvatarById(profile.avatar_emoji);
  const editingAvatar = getAvatarById(editAvatar);

  const handleSaveProfile = async () => {
    try {
      await updateProfile({ bio: editBio, avatar_emoji: editAvatar });
      setEditing(false);
      toast.success('Profile updated!');
    } catch (err: any) { toast.error(err.message); }
  };

  const handlePin = async (cardId: string) => {
    try {
      await pinCard.mutateAsync({ cardId, order: (pinnedCards || []).length });
      toast.success('Card pinned!');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleUnpin = async (cardId: string) => {
    try {
      await unpinCard.mutateAsync(cardId);
      toast.success('Card unpinned');
    } catch (err: any) { toast.error(err.message); }
  };

  const inkCounts: Record<string, number> = {};
  (collection || []).forEach(c => {
    if (c.card?.ink_color) inkCounts[c.card.ink_color] = (inkCounts[c.card.ink_color] || 0) + (c.quantity || 1);
  });
  const topInk = Object.entries(inkCounts).sort((a, b) => b[1] - a[1]);

  return (
    <div className="px-4 pb-4">
      {/* Profile header */}
      <div className="mt-3 rounded-xl border border-border bg-card p-5">
        <div className="flex items-start gap-4">
          {editing ? (
            <button onClick={() => setShowAvatarPicker(true)} className="relative flex-shrink-0">
              {editingAvatar ? (
                <img src={editingAvatar.src} alt={editingAvatar.label} className="h-16 w-16 rounded-full object-cover border-2 border-primary" />
              ) : (
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-3xl border-2 border-primary">{editAvatar}</span>
              )}
              <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">✏️</span>
            </button>
          ) : (
            currentAvatar ? (
              <img src={currentAvatar.src} alt={currentAvatar.label} className="h-16 w-16 rounded-full object-cover flex-shrink-0 border-2 border-primary/30" />
            ) : (
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-3xl flex-shrink-0">{profile.avatar_emoji || '🧙'}</span>
            )
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-foreground">{profile.username}</h2>
              {!editing && (
                <div className="flex items-center gap-2">
                  <button onClick={() => { setEditInks(initialInks); setShowSettings(true); }} aria-label="Settings" className="text-muted-foreground hover:text-primary transition-colors">
                    <Settings className="h-4 w-4" />
                  </button>
                  <button onClick={() => { setEditing(true); setEditBio(profile.bio || ''); setEditAvatar(profile.avatar_emoji || 'sorcerer'); }} aria-label="Edit profile" className="text-muted-foreground hover:text-primary transition-colors">
                    <Edit2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground font-body mt-1">
              {profile.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{profile.location}</span>}
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{joinDate}</span>
            </div>
            {initialInks.length > 0 && (
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                {initialInks.map((ink) => (
                  <span key={ink} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5">
                    <InkDot ink={ink} size="sm" />
                    <span className="text-[10px] text-muted-foreground font-body">{ink}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        {editing ? (
          <div className="mt-4">
            <textarea value={editBio} onChange={e => setEditBio(e.target.value)} placeholder="Tell us about yourself..." rows={2} className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
            <div className="flex gap-2 mt-2">
              <button onClick={handleSaveProfile} className="flex-1 flex items-center justify-center gap-1 rounded-lg bg-primary py-2 text-xs font-medium text-primary-foreground font-body"><Check className="h-3 w-3" />Save</button>
              <button onClick={() => setEditing(false)} className="flex-1 rounded-lg border border-border py-2 text-xs font-medium text-muted-foreground font-body">Cancel</button>
            </div>
          </div>
        ) : profile.bio ? (
          <p className="text-sm text-foreground/80 font-body mt-3">{profile.bio}</p>
        ) : null}
      </div>

      {/* Stats */}
      <div className="mt-3 grid grid-cols-4 gap-2">
        {[
          { label: 'Total', value: totalCards },
          { label: 'Unique', value: uniqueCards },
          { label: 'Enchanted', value: enchantedCount },
          { label: 'Sets', value: setsOwned },
        ].map(stat => (
          <div key={stat.label} className="rounded-xl border border-border bg-card p-3 text-center">
            <p className="font-display text-xl font-bold text-primary">{stat.value}</p>
            <p className="text-[9px] text-muted-foreground font-body">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Match record */}
      <MatchHistoryCard />
      <DeckBreakdownCard />



      {topInk.length > 0 && (
        <div className="mt-3 rounded-xl border border-border bg-card p-4">
          <h3 className="text-xs font-medium text-foreground font-body mb-3 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> Ink Distribution
          </h3>
          <div className="space-y-2">
            {topInk.map(([ink, count]) => {
              const pct = totalCards > 0 ? Math.round((count / totalCards) * 100) : 0;
              return (
                <div key={ink} className="flex items-center gap-2">
                  <InkDot ink={ink as InkColor} size="md" />
                  <span className="text-xs text-foreground font-body w-16">{ink}</span>
                  <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                    <div className={`h-full rounded-full bg-ink-${ink.toLowerCase()} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[10px] text-muted-foreground font-body w-8 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Achievements */}
      <div className="mt-3 rounded-xl border border-border bg-card p-4">
        <h3 className="text-xs font-medium text-foreground font-body mb-3 flex items-center gap-1.5">
          <Trophy className="h-3.5 w-3.5 text-primary" /> Achievements ({unlockedAchievements.length}/{ACHIEVEMENTS.length})
        </h3>
        <div className="flex flex-wrap gap-2">
          {ACHIEVEMENTS.map(a => {
            const unlocked = unlockedAchievements.includes(a);
            return (
              <div key={a.id} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-body transition-all ${unlocked ? 'bg-primary/15 text-primary border border-primary/30' : 'bg-secondary text-muted-foreground/40 border border-border'}`}>
                <span className={unlocked ? '' : 'grayscale opacity-40'}>{a.emoji}</span>
                {a.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pinned Cards Showcase */}
      <div className="mt-3 rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-medium text-foreground font-body flex items-center gap-1.5">
            <Pin className="h-3.5 w-3.5 text-primary" /> Showcase
          </h3>
          {(pinnedCards || []).length < 6 && collection && collection.length > 0 && (
            <button onClick={() => setShowPinPicker(true)} className="flex items-center gap-1 text-[10px] text-primary font-body">+ Pin Card</button>
          )}
        </div>
        {(pinnedCards || []).length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            {pinnedCards!.map(pin => (
              <div key={pin.id} className="relative group rounded-xl border border-primary/20 bg-secondary p-1.5">
                <CardImage src={pin.card?.image_url || null} alt={pin.card?.name || ''} inkColor={pin.card?.ink_color as InkColor} enchanted={pin.card?.enchanted || false} showGlow className="aspect-[2/3]" />
                <p className="truncate text-[9px] text-foreground font-body mt-1 text-center">{pin.card?.name}</p>
                <button onClick={() => handleUnpin(pin.card_id)} aria-label={`Unpin ${pin.card?.name || 'card'}`} className="absolute top-0 right-0 flex h-5 w-5 items-center justify-center rounded-full bg-destructive/80 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-muted-foreground font-body text-center py-6">Pin up to 6 favorite cards to show off your best pulls</p>
        )}
      </div>

      {/* Sign Out */}
      <button onClick={signOut} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-card py-3.5 text-sm font-medium text-muted-foreground font-body hover:text-foreground transition-colors">
        <LogOut className="h-4 w-4" /> Sign Out
      </button>

      {/* Pin Picker Modal */}
      {showPinPicker && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={() => setShowPinPicker(false)}>
          <div className="animate-slide-up w-full max-w-[430px] max-h-[60vh] overflow-y-auto rounded-t-2xl border border-border bg-card p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-base font-semibold text-foreground">Pin a Card</h3>
              <button onClick={() => setShowPinPicker(false)} aria-label="Close pin picker" className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {(collection || []).filter(item => item.card && !pinnedCardIds.has(item.card.id)).map(item => (
                <button key={item.id} onClick={() => { handlePin(item.card!.id); setShowPinPicker(false); }} className="rounded-lg border border-border p-1 hover:border-primary transition-colors">
                  <CardImage src={item.card!.image_url} alt={item.card!.name} className="aspect-[2/3] rounded" />
                  <p className="truncate text-[8px] text-muted-foreground font-body mt-0.5 text-center">{item.card!.name}</p>
                </button>
              ))}
            </div>
            {(collection || []).filter(item => item.card && !pinnedCardIds.has(item.card.id)).length === 0 && (
              <p className="text-sm text-muted-foreground font-body py-6 text-center">No cards available to pin</p>
            )}
          </div>
        </div>
      )}

      {/* Avatar Picker Modal */}
      {showAvatarPicker && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={() => setShowAvatarPicker(false)}>
          <div className="animate-slide-up w-full max-w-[430px] max-h-[70vh] overflow-y-auto rounded-t-2xl border border-border bg-card p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-base font-semibold text-foreground">Choose Your Avatar</h3>
              <button onClick={() => setShowAvatarPicker(false)} aria-label="Close avatar picker" className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {AVATAR_OPTIONS.map(avatar => (
                <button
                  key={avatar.id}
                  onClick={() => { setEditAvatar(avatar.id); setShowAvatarPicker(false); }}
                  className={`relative rounded-xl overflow-hidden border-2 transition-all ${editAvatar === avatar.id ? 'border-primary ring-2 ring-primary/30 scale-105' : 'border-border hover:border-primary/50'}`}
                >
                  <img src={avatar.src} alt={avatar.label} loading="lazy" width={512} height={512} className="w-full aspect-square object-cover" />
                  <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-background/90 to-transparent px-1 py-0.5">
                    <span className="text-[8px] text-foreground font-body leading-tight block truncate">{avatar.label}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={() => setShowSettings(false)}>
          <div className="animate-slide-up w-full max-w-[430px] max-h-[80vh] overflow-y-auto rounded-t-2xl border border-border bg-card p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
                <Settings className="h-4 w-4" /> Settings
              </h3>
              <button onClick={() => setShowSettings(false)} aria-label="Close settings" className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground font-body">Favorite Inks (pick 2)</span>
                  <span className="text-[10px] text-muted-foreground/70 font-body">{editInks.length}/2</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {INK_OPTIONS.map((ink) => {
                    const selected = editInks.includes(ink);
                    return (
                      <button
                        key={ink}
                        type="button"
                        onClick={() => toggleEditInk(ink)}
                        aria-pressed={selected}
                        className={`flex flex-col items-center gap-1 rounded-lg p-2 transition-colors ${selected ? 'bg-secondary ring-1 ring-primary' : 'hover:bg-secondary'}`}
                      >
                        <InkDot ink={ink} size="md" />
                        <span className="text-[9px] text-muted-foreground font-body">{ink}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[10px] text-muted-foreground font-body">
                  Scout uses these to personalize your in-app overview.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={saveInks}
                  disabled={savingInks || editInks.length !== 2}
                  className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground font-body disabled:opacity-50"
                >
                  {savingInks ? 'Saving…' : 'Save Inks'}
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className="flex-1 rounded-lg border border-border py-2.5 text-sm font-medium text-muted-foreground font-body"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
